import { useState } from "react";
import { ChevronRight, Loader2, Plus, Settings } from "lucide-react";
import { formatCurrency } from "@vaulzo/core/constants";
import { MONTHS_LONG, categoryLabel, generalBreakdown, monthView, yearView, monthOfDate, todayISO } from "@vaulzo/core/domain/expenses";
import {
  useExpensesYear,
  useSaveExpense,
  useDeleteExpense,
  useSaveExpenseGroup,
  useDeleteExpenseGroup,
  useSaveRecurringExpense,
  useDeleteRecurringExpense,
} from "@vaulzo/core/hooks/useExpenses";
import ConfirmDialog from "@/components/ConfirmDialog";
import ExpenseDialog from "./ExpenseDialog";
import GeneralModal from "./GeneralModal";
import GroupModal from "./GroupModal";
import RecurringModal from "./RecurringModal";
import { ArrowButton, Pill, RoundButton } from "./ui";

const thisYear = () => new Date().getFullYear();
const thisMonth = () => new Date().getMonth() + 1;

/**
 * Expenses page: the year strip and its twelve month cells, then the
 * month as a short list (General spending, groups, the standalone
 * categories) with the total and the recurring entries still to come.
 * Owns every dialog; data comes from one query per year.
 */
export default function Expenses() {
  const [year, setYear] = useState(thisYear);
  const [month, setMonth] = useState(thisMonth);
  const query = useExpensesYear(year);
  const data = query.data;

  const saveExpense = useSaveExpense();
  const deleteExpense = useDeleteExpense();
  const saveGroup = useSaveExpenseGroup();
  const deleteGroup = useDeleteExpenseGroup();
  const saveRule = useSaveRecurringExpense();
  const deleteRule = useDeleteRecurringExpense();

  // Which modal is open: null | "general" | { groupId } | "recurring".
  const [modal, setModal] = useState(null);
  const [dialog, setDialog] = useState(null); // { editing?, preset? }
  const [pending, setPending] = useState(null); // { kind: "expense"|"group"|"rule", item }
  const [busy, setBusy] = useState(false);

  const yv = yearView(data, year);
  const mv = monthView(data, year, month);
  const prevTotal = month > 1 ? monthView(data, year, month - 1).total : 0;
  const change = mv.total - prevTotal;
  const groupRow = modal && modal.groupId ? mv.groups.find((g) => g.group.id === modal.groupId) || null : null;
  const dateForNew = () => {
    const today = todayISO();
    return monthOfDate(today) === mv.key ? today : `${mv.key}-01`;
  };

  const saveWith = (payload, id, onSuccess) =>
    saveExpense.mutateAsync({ payload, id }).then(onSuccess).catch(() => {});
  const confirmDelete = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      if (pending.kind === "expense") await deleteExpense.mutateAsync(pending.item.id);
      else if (pending.kind === "group") {
        await deleteGroup.mutateAsync(pending.item.id);
        setModal(null);
      } else await deleteRule.mutateAsync(pending.item.id);
      setPending(null);
      if (pending.kind === "expense" && dialog?.editing?.id === pending.item.id) setDialog(null);
    } catch {
      /* toast raised by the hook */
    } finally {
      setBusy(false);
    }
  };

  const rows = [];
  if (mv.general.length) {
    const top = generalBreakdown(mv.general).slice(0, 3).map((c) => `${c.label} ${formatCurrency(c.total)}`);
    rows.push({ key: "general", name: "General spending", meta: `${mv.general.length} entries · ${top.join(" · ")}`, tone: "general", pill: "General", value: mv.generalTotal, opens: true, onClick: () => setModal("general") });
  }
  for (const g of mv.groups) rows.push({ key: g.group.id, name: g.group.name, meta: `${g.items.length} ${g.items.length === 1 ? "entry" : "entries"}`, tone: "group", pill: "Group", value: g.total, opens: true, onClick: () => setModal({ groupId: g.group.id }) });
  for (const e of mv.singles) rows.push({ key: e.id, name: e.name, meta: `${Number(e.date.slice(8))} ${MONTHS_LONG[month - 1].slice(0, 3)} ${year}`, tone: "single", pill: categoryLabel(e.category), value: e.amount, opens: false, onClick: () => setDialog({ editing: e }) });

  const isInitialLoad = query.isLoading && !data;

  return (
    <main className="mx-auto max-w-[800px] px-6 pt-14">
      <div className="mb-7 flex items-center justify-between">
        <h1 className="m-0 text-[26px] font-bold tracking-[-0.02em] text-vaulzo-ink">Expenses</h1>
        <div className="flex gap-2">
          <RoundButton label="Recurring expenses" onClick={() => setModal("recurring")}>
            <Settings className="h-[17px] w-[17px]" strokeWidth={1.8} />
          </RoundButton>
          <RoundButton tone="primary" label="Add expense" onClick={() => setDialog({ preset: { date: dateForNew() } })}>
            <Plus className="h-[18px] w-[18px]" strokeWidth={2.2} />
          </RoundButton>
        </div>
      </div>

      {/* Year strip */}
      <div className="mb-[18px] flex items-center justify-between gap-3">
        <ArrowButton dir="left" label="Previous year" onClick={() => setYear(year - 1)} />
        <div className="min-w-0 text-center">
          <div className="num text-[22px] font-bold tracking-[-0.02em] text-vaulzo-ink">{year}</div>
          <div className="num mt-[3px] truncate text-[12px] text-vaulzo-fainter">
            {isInitialLoad ? "Loading…" : yv.monthsWithData ? `${formatCurrency(yv.spent)} spent so far · ${formatCurrency(yv.average)} a month` : `Nothing logged in ${year} yet`}
          </div>
        </div>
        <ArrowButton dir="right" label="Next year" onClick={() => setYear(year + 1)} disabled={year >= thisYear()} />
      </div>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {yv.months.map((m) => {
          const on = m.month === month;
          return (
            <button
              key={m.month}
              type="button"
              onClick={() => setMonth(m.month)}
              className={`flex min-w-0 flex-col items-start gap-[6px] rounded-[14px] border px-3 pb-[11px] pt-3 text-left transition-colors ${
                on ? "border-vaulzo-ink bg-vaulzo-ink" : "border-vaulzo-hairline bg-vaulzo-surface hover:border-vaulzo-input-line"
              }`}
            >
              <span className={`text-[12px] font-semibold ${on ? "text-vaulzo-on-fill/55" : "text-vaulzo-fainter"}`}>{m.label}</span>
              <span className={`num max-w-full truncate text-[14px] font-semibold ${on ? "text-vaulzo-on-fill" : m.isEmpty ? "text-vaulzo-bar" : "text-vaulzo-ink"}`}>{m.isEmpty ? "—" : formatCurrency(m.total)}</span>
              <span className="block h-[3px] w-full rounded-sm">
                <span className="block h-[3px] rounded-sm" style={{ width: `${m.share * 100}%`, backgroundColor: on ? "rgb(var(--vz-on-fill) / 0.5)" : "rgb(var(--vz-band))" }} />
              </span>
            </button>
          );
        })}
      </div>

      {/* Month */}
      <div className="mt-10 flex items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[12px] uppercase tracking-[0.14em] text-vaulzo-fainter">Spent in {MONTHS_LONG[month - 1]}</div>
          <div className="num mt-3 text-[40px] font-bold leading-[0.95] tracking-[-0.04em] text-vaulzo-ink">{formatCurrency(mv.total)}</div>
          <div className={`num mt-3 text-sm font-semibold ${mv.isFuture && mv.isEmpty ? "text-vaulzo-faint" : prevTotal > 0 ? (change > 0 ? "text-vaulzo-danger" : "text-vaulzo-green") : "text-vaulzo-faint"}`}>
            {mv.isFuture && mv.isEmpty
              ? "Nothing yet"
              : prevTotal > 0
                ? `${change >= 0 ? "↑" : "↓"} ${formatCurrency(Math.abs(change))} · ${Math.abs((change / prevTotal) * 100).toFixed(1)}% vs ${MONTHS_LONG[month - 2]}`
                : month === 1
                  ? "First month of the year"
                  : ""}
          </div>
        </div>
        <div className="flex flex-none gap-2">
          <ArrowButton dir="left" label="Previous month" onClick={() => setMonth(month - 1)} disabled={month <= 1} />
          <ArrowButton dir="right" label="Next month" onClick={() => setMonth(month + 1)} disabled={month >= 12} />
        </div>
      </div>

      {isInitialLoad ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-vaulzo-accent" aria-hidden />
        </div>
      ) : (
        <>
          {(rows.length > 0 || !mv.upcoming.length) && (
            <div className="mt-7 overflow-hidden rounded-[18px] border border-vaulzo-hairline bg-vaulzo-surface">
              {rows.length === 0 && <div className="px-5 py-12 text-center text-sm text-vaulzo-subtle">Nothing logged for {MONTHS_LONG[month - 1]} yet.</div>}
              {rows.map((r, i) => (
                <div key={r.key} className={`flex items-stretch ${i ? "border-t border-vaulzo-row" : ""}`}>
                  <button type="button" onClick={r.onClick} className="flex min-w-0 flex-1 items-center gap-3 px-[18px] py-4 text-left transition-colors hover:bg-vaulzo-bg">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-semibold text-vaulzo-ink">{r.name}</span>
                      <span className="num mt-[3px] block truncate text-[12px] text-vaulzo-fainter">{r.meta}</span>
                      <span className="mt-[7px] flex flex-wrap items-center gap-2">
                        <Pill tone={r.tone}>{r.pill}</Pill>
                      </span>
                    </span>
                    <span className="num whitespace-nowrap text-right text-[15px] font-bold text-vaulzo-ink">{formatCurrency(r.value)}</span>
                    <span className="flex w-[14px] flex-none justify-center text-vaulzo-muted">{r.opens && <ChevronRight className="h-[14px] w-[14px]" strokeWidth={1.8} />}</span>
                  </button>
                </div>
              ))}
            </div>
          )}
          {rows.length > 0 && (
            <div className="mt-5 flex items-baseline justify-between border-t-2 border-vaulzo-ink px-1 pt-5">
              <span className="text-[15px] font-semibold text-vaulzo-ink">Total</span>
              <span className="num text-[22px] font-bold tracking-[-0.02em] text-vaulzo-ink">{formatCurrency(mv.total)}</span>
            </div>
          )}
          {mv.upcoming.length > 0 && (
            <div className="num mt-[18px] px-1 text-[13px] leading-relaxed text-vaulzo-fainter">
              {formatCurrency(mv.upcomingTotal)} {mv.isFuture ? "arrives automatically" : "still to come this month"} · {mv.upcoming.map((u) => u.rule.name).join(", ")}
            </div>
          )}
        </>
      )}

      <GeneralModal
        open={modal === "general"}
        onClose={() => setModal(null)}
        general={mv.general}
        year={year}
        month={month}
        onAdd={(preset = {}) => setDialog({ preset: { date: preset.date || dateForNew(), category: preset.category || "food" } })}
        onEdit={(e) => setDialog({ editing: e })}
      />
      <GroupModal
        open={!!groupRow}
        onClose={() => setModal(null)}
        row={groupRow}
        renaming={saveGroup.isPending}
        onAddEntry={() => setDialog({ preset: { date: dateForNew(), category: "travel", groupId: groupRow.group.id } })}
        onEditEntry={(e) => setDialog({ editing: e })}
        onDeleteEntry={(e) => setPending({ kind: "expense", item: e })}
        onRename={(name, done) => saveGroup.mutateAsync({ id: groupRow.group.id, payload: { name, month: groupRow.group.month } }).then(done).catch(() => {})}
        onDeleteGroup={() => setPending({ kind: "group", item: groupRow.group })}
      />
      <RecurringModal
        open={modal === "recurring"}
        onClose={() => setModal(null)}
        rules={data?.recurring || []}
        saving={saveRule.isPending}
        onSave={(payload, id, done) => saveRule.mutateAsync({ payload, id }).then(done).catch(() => {})}
        onDelete={(r) => setPending({ kind: "rule", item: r })}
      />
      <ExpenseDialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        data={data}
        editing={dialog?.editing || null}
        preset={dialog?.preset || {}}
        isSubmitting={saveExpense.isPending}
        onSubmit={(payload) => saveWith(payload, dialog?.editing?.id, () => setDialog(null))}
        onDelete={dialog?.editing ? () => setPending({ kind: "expense", item: dialog.editing }) : null}
      />
      <ConfirmDialog
        open={!!pending}
        title={pending?.kind === "group" ? "Delete group?" : pending?.kind === "rule" ? "Delete recurring expense?" : "Delete expense?"}
        message={
          pending?.kind === "group"
            ? `"${pending.item.name}" will be removed. Its entries stay as normal expenses on their own dates.`
            : pending?.kind === "rule"
              ? `"${pending.item.name}" will no longer be added each month. Past months keep their entries.`
              : pending
                ? `"${pending.item.name}" will be removed from this month.`
                : ""
        }
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={confirmDelete}
      />
    </main>
  );
}
